import React, {
    createContext,
    useContext,
    useMemo,
    useState,
    ReactNode,
    useEffect,
    useCallback,
} from 'react';
import { ThemeProvider as MUIThemeProvider, CssBaseline } from '@mui/material';
import { getTheme } from './Theme';
import { updateTheme } from '../../reducers/reducer';
import { useDispatch } from 'react-redux';
import { AuthService } from '../../services/AuthService';
import { useSelector } from 'react-redux';
import * as selector from "../../selectors/selectors"


type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
    mode: ThemeMode;
    resolvedTheme: 'light' | 'dark';
}

interface ThemeContextType extends ThemeState {
    toggleTheme: (selectedMode: ThemeMode) => void;
    isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useThemeContext must be used within ThemeProviderWrapper');
    }
    return context;
};

// Constants
const STORAGE_KEY = 'themeMode';
const DARK_MODE_CLASS = 'dark-mode';
const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';

const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window === 'undefined') return 'light';
    if (AuthService.isUserLoggedIn()) {
        return window.matchMedia?.(SYSTEM_THEME_QUERY)?.matches ? 'dark' : 'light';
    } else {
        return window.matchMedia?.(SYSTEM_THEME_QUERY)?.matches ? 'light' : 'light';
    }
};

const getStoredTheme = (): ThemeMode => {
    if (typeof window === 'undefined') return 'system';
    try {
        const stored = localStorage.getItem(STORAGE_KEY) as ThemeMode;


        return ['light', 'dark', 'system'].includes(stored) ? stored : 'system';
    } catch {
        return 'system';
    }
};

const storeTheme = (mode: ThemeMode): void => {
    if (typeof window === 'undefined') return;
    try {
        localStorage.setItem(STORAGE_KEY, mode);
    } catch {
    }
};

const resolveTheme = (mode: ThemeMode): 'light' | 'dark' => {
    return mode === 'system' ? getSystemTheme() : mode;
};
export const ThemeProviderWrapper = ({ children }: { children: ReactNode }) => {
    const IsDarkMode: boolean = useSelector(selector.isDarkMode)
    const [themeState, setThemeState] = useState<ThemeState>(() => {
        const initialMode = IsDarkMode ? "dark" : "light"

        return {
            mode: initialMode,
            resolvedTheme: resolveTheme(initialMode),
        };
    });

    const dispatch = useDispatch();

    const applyTheme = useCallback((theme: 'light' | 'dark') => {
        const isDark = theme === 'dark';

        dispatch(updateTheme(isDark));

        if (typeof document !== 'undefined') {
            document.documentElement.classList.toggle(DARK_MODE_CLASS, isDark);
            document.body.classList.toggle(DARK_MODE_CLASS, isDark);
        }
    }, [dispatch]);

    // Theme toggle function
    const toggleTheme = useCallback((selectedMode: ThemeMode) => {
        const newResolvedTheme = resolveTheme(selectedMode);


        document.startViewTransition(() => {
            setThemeState({
                mode: selectedMode,
                resolvedTheme: newResolvedTheme,
            });
            // storeTheme(selectedMode);
            applyTheme(newResolvedTheme);
        })


    }, [applyTheme]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;

        const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);

        const handleSystemThemeChange = (e: MediaQueryListEvent) => {
            if (themeState.mode === 'system') {
                const newSystemTheme: 'light' | 'dark' = e.matches ? 'dark' : 'light';

                setThemeState(prev => ({
                    ...prev,
                    resolvedTheme: newSystemTheme,
                }));

                applyTheme(newSystemTheme);
            }
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleSystemThemeChange);
        } else {
            mediaQuery.addListener(handleSystemThemeChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
            } else {
                mediaQuery.removeListener(handleSystemThemeChange);
            }
        };
    }, [themeState.mode, applyTheme]);

    useEffect(() => {
        applyTheme(themeState.resolvedTheme);
    }, []);

    const theme = useMemo(() => getTheme(themeState.resolvedTheme), [themeState.resolvedTheme]);

    const contextValue = useMemo(() => ({
        ...themeState,
        toggleTheme,
        isDark: themeState.resolvedTheme === 'dark',
    }), [themeState, toggleTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            <MUIThemeProvider theme={theme}>
                {/* <CssBaseline /> */}
                {children}
            </MUIThemeProvider>
        </ThemeContext.Provider>
    );
};